import { useQueryClient } from '@tanstack/react-query';
import { Check, FileText, Loader2, Palette, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

import {
  getLayoutsDocumentoControllerListarQueryKey,
  useLayoutsDocumentoControllerAtivar,
  useLayoutsDocumentoControllerAtualizar,
  useLayoutsDocumentoControllerCriar,
  useLayoutsDocumentoControllerEnviarMarcaDagua,
  useLayoutsDocumentoControllerExcluir,
  useLayoutsDocumentoControllerListar,
} from '@/api/generated/layouts-documento/layouts-documento';
import {
  LayoutDocumentoResponseMarcaDaguaTipo as Tipo,
  type LayoutDocumentoResponse,
} from '@/api/generated/model';
import { ApiError, pdfPorPost } from '@/api/http-client';
import { useAuth } from '@/features/auth/hooks/useAuth';
import {
  ControleDeMarcaDagua,
  type EstadoDaMarcaDagua,
} from '@/features/documentos/components/ControleDeMarcaDagua';
import { MiniaturaDeLayout } from '@/features/documentos/components/MiniaturaDeLayout';
import { PreviaDoLayout } from '@/features/documentos/components/PreviaDoLayout';
import { SeletorDeCor } from '@/shared/ui/seletor-de-cor';
import { SeletorDeModelo } from '@/features/documentos/components/SeletorDeModelo';
import { abrirPdfDeBlob } from '@/features/documentos/lib/abrirPdfDeBlob';
import { ROTULO_MODELO, type Modelo } from '@/features/documentos/rotulos';
import { COR_PADRAO, HEX } from '@/shared/lib/cores';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { EstadoCarregando, EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { toast } from '@/shared/ui/sonner';

interface Props {
  aberto: boolean;
  aoFechar: () => void;
}

/**
 * ⚠️ Tipo explícito, não inferido do `VAZIO` abaixo.
 *
 * Sem ele o TypeScript estreita o estado inicial para os literais dele
 * (`tipo: 'NENHUMA'`, `arquivoId: null`) e passar a `TEXTO` ou receber o id de
 * uma imagem vira erro de atribuição.
 */
interface FormularioDeLayout {
  nome: string;
  modelo: Modelo;
  cor: string;
  marcaDagua: EstadoDaMarcaDagua;
}

/** O `documento` da prévia — mesmo enum do `previaDeLayoutSchema` no backend. */
type DocumentoDePrevia = 'RECEITUARIO' | 'RECEITUARIO_CONTROLE_ESPECIAL' | 'REQUISICAO';

const VAZIO: FormularioDeLayout = {
  nome: '',
  modelo: 'CLASSICO',
  cor: COR_PADRAO,
  marcaDagua: {
    tipo: Tipo.NENHUMA,
    texto: '',
    arquivoId: null,
    url: null,
    opacidade: 8,
    inclinacao: -45,
  },
};

/**
 * Configuração de layout dos documentos.
 *
 * ⚠️ Não confundir com "Modelos", o botão vizinho na página de receituários:
 * lá são textos salvos, aqui é a APARÊNCIA do papel. Os ícones (`FileType2` ×
 * `Palette`) e as descrições de cada diálogo é que separam os dois.
 *
 * Um diálogo só, com a lista e o editor embutido — mesma decisão de
 * `TemplatesDialogo`: o layout não tem vida fora dos documentos, e uma rota
 * separada obrigaria a sair do fluxo para voltar.
 *
 * ⚠️ **Só um layout fica ativo**, e quem garante isso é o schema (uma FK
 * escalar em `profiles`), não esta tela. Aqui o "Usar este" é só o botão que
 * chama a rota.
 */
export function LayoutDocumentoDialogo({ aberto, aoFechar }: Props) {
  const queryClient = useQueryClient();
  const { me } = useAuth();

  const { data, isPending, isError, refetch } = useLayoutsDocumentoControllerListar({
    query: { enabled: aberto },
  });

  const criar = useLayoutsDocumentoControllerCriar();
  const atualizar = useLayoutsDocumentoControllerAtualizar();
  const ativar = useLayoutsDocumentoControllerAtivar();
  const excluir = useLayoutsDocumentoControllerExcluir();
  const enviarImagem = useLayoutsDocumentoControllerEnviarMarcaDagua();

  const [emEdicao, setEmEdicao] = useState<LayoutDocumentoResponse | null>(null);
  const [criando, setCriando] = useState(false);
  const [form, setForm] = useState<FormularioDeLayout>(VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [gerandoPrevia, setGerandoPrevia] = useState<DocumentoDePrevia | null>(null);

  const editorAberto = criando || Boolean(emEdicao);

  function abrirNovo() {
    setEmEdicao(null);
    setForm(VAZIO);
    setCriando(true);
  }

  function abrirEdicao(layout: LayoutDocumentoResponse) {
    setCriando(false);
    setEmEdicao(layout);
    setForm({
      nome: layout.nome,
      modelo: layout.modelo,
      cor: layout.corDeDestaque,
      marcaDagua: {
        tipo: layout.marcaDaguaTipo,
        texto: layout.marcaDaguaTexto ?? '',
        arquivoId: layout.marcaDaguaArquivoId,
        url: layout.marcaDaguaUrl,
        opacidade: layout.marcaDaguaOpacidade,
        inclinacao: layout.marcaDaguaInclinacao,
      },
    });
  }

  function fecharEditor() {
    setCriando(false);
    setEmEdicao(null);
  }

  async function invalidar() {
    await queryClient.invalidateQueries({
      queryKey: getLayoutsDocumentoControllerListarQueryKey(),
    });
  }

  /** O corpo que o servidor espera, montado do formulário. É o mesmo da prévia. */
  function corpo() {
    const { marcaDagua } = form;

    return {
      modelo: form.modelo,
      corDeDestaque: form.cor,
      marcaDaguaTipo: marcaDagua.tipo,
      marcaDaguaTexto: marcaDagua.tipo === Tipo.TEXTO ? marcaDagua.texto.trim() : null,
      marcaDaguaArquivoId: marcaDagua.tipo === Tipo.IMAGEM ? marcaDagua.arquivoId : null,
      marcaDaguaOpacidade: marcaDagua.opacidade,
      marcaDaguaInclinacao: marcaDagua.inclinacao,
    };
  }

  /** O que impede salvar ou pré-visualizar, e por quê. `null` = está pronto. */
  function impedimento(): string | null {
    if (!HEX.test(form.cor)) return 'Informe a cor no formato #RRGGBB.';
    if (form.marcaDagua.tipo === Tipo.TEXTO && !form.marcaDagua.texto.trim())
      return "Escreva o texto da marca d'água.";
    if (form.marcaDagua.tipo === Tipo.IMAGEM && !form.marcaDagua.arquivoId)
      return "Envie a imagem da marca d'água.";
    return null;
  }

  async function salvar() {
    if (form.nome.trim().length < 2) {
      toast.error('Dê um nome com ao menos 2 caracteres.');
      return;
    }

    const erro = impedimento();
    if (erro) {
      toast.error(erro);
      return;
    }

    setSalvando(true);
    try {
      if (emEdicao) {
        await atualizar.mutateAsync({
          layoutId: emEdicao.id,
          data: { nome: form.nome.trim(), ...corpo() },
        });
      } else {
        await criar.mutateAsync({ data: { nome: form.nome.trim(), ...corpo() } });
      }
      await invalidar();
      toast.success(emEdicao ? 'Layout atualizado.' : 'Layout criado.');
      fecharEditor();
    } catch (erroApi) {
      toast.error(erroApi instanceof ApiError ? erroApi.message : 'Não foi possível salvar.');
    } finally {
      setSalvando(false);
    }
  }

  /**
   * Renderiza o PDF de amostra com o que está no formulário — antes de salvar.
   *
   * Não passa pelo hook gerado de propósito: o mutator faz `JSON.parse` da
   * resposta, e o binário quebraria no parse. Ver `pdfPorPost`.
   */
  async function gerarPrevia(documento: DocumentoDePrevia) {
    const erro = impedimento();
    if (erro) {
      toast.error(erro);
      return;
    }

    setGerandoPrevia(documento);
    try {
      await abrirPdfDeBlob(() =>
        pdfPorPost('/v1/layouts-documento/previa', { ...corpo(), documento }),
      );
    } catch (erroApi) {
      toast.error(
        erroApi instanceof ApiError ? erroApi.message : 'Não foi possível gerar a prévia.',
      );
    } finally {
      setGerandoPrevia(null);
    }
  }

  async function subirImagem(arquivo: File) {
    try {
      const { arquivoId, url } = await enviarImagem.mutateAsync({ data: { arquivo } });
      setForm((f) => ({ ...f, marcaDagua: { ...f.marcaDagua, arquivoId, url } }));
      toast.success("Imagem da marca d'água enviada.");
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível enviar a imagem.');
    }
  }

  async function usarEste(layout: LayoutDocumentoResponse) {
    try {
      await ativar.mutateAsync({ layoutId: layout.id });
      await invalidar();
      toast.success(`Os documentos passam a sair no layout "${layout.nome}".`);
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível ativar.');
    }
  }

  async function remover(layout: LayoutDocumentoResponse) {
    try {
      await excluir.mutateAsync({ layoutId: layout.id });
      await invalidar();
      toast.success(
        layout.ativo
          ? 'Layout excluído. Os documentos voltam ao papel padrão.'
          : 'Layout excluído.',
      );
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível excluir.');
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Configuração de layout</DialogTitle>
          <DialogDescription>
            Escolha o papel timbrado dos seus receituários e requisições. Você pode guardar
            vários — o marcado como <strong className="font-medium">Em uso</strong> é o que
            gera os documentos.
          </DialogDescription>
        </DialogHeader>

        {editorAberto ? (
          <div className="space-y-5 max-h-[65vh] overflow-y-auto pr-1">
            <Campo id="nome-layout" rotulo="Nome do layout" obrigatorio dica="Só você vê. Ex.: “Papel da clínica”.">
              <Input
                id="nome-layout"
                value={form.nome}
                maxLength={60}
                onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
                className={CLASSE_CONTROLE}
              />
            </Campo>

            <SeletorDeModelo
              modelo={form.modelo}
              cor={form.cor}
              aoEscolher={(modelo) => setForm((f) => ({ ...f, modelo }))}
            />

            <SeletorDeCor cor={form.cor} aoMudar={(cor) => setForm((f) => ({ ...f, cor }))} />

            <ControleDeMarcaDagua
              valor={form.marcaDagua}
              aoMudar={(parcial) =>
                setForm((f) => ({ ...f, marcaDagua: { ...f.marcaDagua, ...parcial } }))
              }
              aoEnviarImagem={(arquivo) => void subirImagem(arquivo)}
              enviando={enviarImagem.isPending}
            />

            {me && (
              <PreviaDoLayout
                modelo={form.modelo}
                cor={HEX.test(form.cor) ? form.cor : COR_PADRAO}
                marcaDagua={form.marcaDagua}
                me={me}
              />
            )}

            <DialogFooter className="flex-col sm:flex-row sm:justify-between gap-2">
              {/* Botões, não abas: os documentos têm corpos bem diferentes,
                  e um preset pode ficar bom num e apertado no outro. Ver só
                  um é ver metade. */}
              <div className="flex flex-wrap gap-2">
                <BotaoDePrevia
                  rotulo="Prévia: receituário"
                  ocupado={gerandoPrevia === 'RECEITUARIO'}
                  desabilitado={Boolean(gerandoPrevia) || enviarImagem.isPending}
                  aoClicar={() => void gerarPrevia('RECEITUARIO')}
                />
                <BotaoDePrevia
                  rotulo="Prévia: controle especial"
                  ocupado={gerandoPrevia === 'RECEITUARIO_CONTROLE_ESPECIAL'}
                  desabilitado={Boolean(gerandoPrevia) || enviarImagem.isPending}
                  aoClicar={() => void gerarPrevia('RECEITUARIO_CONTROLE_ESPECIAL')}
                />
                <BotaoDePrevia
                  rotulo="Prévia: requisição"
                  ocupado={gerandoPrevia === 'REQUISICAO'}
                  desabilitado={Boolean(gerandoPrevia) || enviarImagem.isPending}
                  aoClicar={() => void gerarPrevia('REQUISICAO')}
                />
              </div>

              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={fecharEditor}>
                  Voltar
                </Button>
                <Button onClick={() => void salvar()} disabled={salvando}>
                  {salvando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Salvar
                </Button>
              </div>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={abrirNovo}>
                <Plus className="w-4 h-4" />
                Novo layout
              </Button>
            </div>

            {isPending ? (
              <EstadoCarregando rotulo="Carregando layouts..." />
            ) : isError || !data ? (
              <EstadoErro aoTentarDeNovo={() => void refetch()} />
            ) : data.itens.length === 0 ? (
              <EstadoVazio
                icone={Palette}
                titulo="Nenhum layout salvo"
                descricao="Seus documentos saem no papel padrão. Crie um layout para personalizar cor, modelo e marca d'água."
              />
            ) : (
              <>
                {data.padraoEmUso && (
                  <p className="rounded-md border border-brand/20 bg-brand/5 px-3 py-2 text-xs text-muted-foreground">
                    Nenhum layout em uso — os documentos saem no papel padrão do sistema.
                    Escolha um com <strong className="font-medium">Usar este</strong>.
                  </p>
                )}

                <ul className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
                  {data.itens.map((layout) => (
                    <li
                      key={layout.id}
                      className="flex items-center justify-between gap-3 rounded-md border border-brand/15 px-3 py-2"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 shrink-0">
                          <MiniaturaDeLayout modelo={layout.modelo} cor={layout.corDeDestaque} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {layout.nome}
                            {layout.ativo && (
                              <Badge variant="outline" className="ml-2 border-brand text-brand">
                                Em uso
                              </Badge>
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {ROTULO_MODELO[layout.modelo]}
                            {layout.marcaDaguaTipo !== Tipo.NENHUMA &&
                              ` · marca d'água ${layout.marcaDaguaTipo === Tipo.TEXTO ? `“${layout.marcaDaguaTexto ?? ''}”` : 'em imagem'}`}
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-1 shrink-0">
                        {!layout.ativo && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-1"
                            aria-label={`Usar o layout ${layout.nome}`}
                            disabled={ativar.isPending}
                            onClick={() => void usarEste(layout)}
                          >
                            <Check className="w-4 h-4" />
                            Usar este
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Editar ${layout.nome}`}
                          onClick={() => abrirEdicao(layout)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Excluir ${layout.nome}`}
                          onClick={() => void remover(layout)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={aoFechar}>
                Fechar
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BotaoDePrevia({
  rotulo,
  ocupado,
  desabilitado,
  aoClicar,
}: {
  rotulo: string;
  ocupado: boolean;
  desabilitado: boolean;
  aoClicar: () => void;
}) {
  return (
    <Button type="button" variant="outline" size="sm" disabled={desabilitado} onClick={aoClicar}>
      {ocupado ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <FileText className="w-4 h-4" />
      )}
      {rotulo}
    </Button>
  );
}
